<template>
    <a-modal
        :visible="visible"
        title="Meus filtros"
        :footer="null"
        width="40vw"
        :dialog-style="{ minWidth: '420px', maxWidth: '640px' }"
        @cancel="$emit('close')"
    >
        <div class="saved-filters-header">
            <span class="saved-filters-count">
                {{ items.length }}
                {{ items.length === 1 ? "filtro salvo" : "filtros salvos" }}
            </span>
            <a-button type="primary" size="small" @click="openCreate">
                <i class="fa-solid fa-plus"></i> Salvar filtros atuais
            </a-button>
        </div>

        <a-spin :spinning="loading">
            <div v-if="!items.length" class="saved-filters-empty">
                Nenhum filtro salvo.
            </div>
            <div v-else class="saved-filters-list">
                <div
                    v-for="item in items"
                    :key="item.id"
                    class="saved-filters-item"
                    :class="{ 'is-active': item.id === activeId }"
                    @click="$emit('apply', item)"
                >
                    <span class="saved-filters-name">{{ item.name }}</span>
                    <a-tooltip
                        v-if="item.id === activeId && activeModified"
                        title="Os filtros mudaram depois que este foi aplicado"
                    >
                        <a-tag>Alterado</a-tag>
                    </a-tooltip>
                    <a-icon
                        v-else-if="item.id === activeId"
                        type="check"
                        class="saved-filters-check"
                    />
                    <a-tooltip
                        v-if="item.is_system"
                        title="Filtro do sistema, disponível para todos"
                    >
                        <a-tag color="purple">
                            <a-icon type="lock" /> Sistema
                        </a-tag>
                    </a-tooltip>
                    <a-tooltip
                        v-else-if="item.is_shared"
                        :title="
                            item.is_owner
                                ? 'Compartilhado com a equipe'
                                : `Compartilhado por ${item.owner_name || 'outro usuário'}`
                        "
                    >
                        <a-tag color="blue">Compartilhado</a-tag>
                    </a-tooltip>
                    <span
                        v-if="item.is_owner"
                        class="saved-filters-controls"
                        @click.stop
                    >
                        <a-tooltip title="Editar">
                            <a-icon
                                type="edit"
                                class="saved-filters-action"
                                @click="openEdit(item)"
                            />
                        </a-tooltip>
                        <a-popconfirm
                            title="Excluir este filtro salvo?"
                            ok-text="Excluir"
                            cancel-text="Cancelar"
                            @confirm="remove(item)"
                        >
                            <a-icon
                                type="delete"
                                class="saved-filters-action"
                            />
                        </a-popconfirm>
                    </span>
                </div>
            </div>
        </a-spin>

        <a-modal
            :visible="form.visible"
            :title="form.id ? 'Editar filtro salvo' : 'Salvar filtros'"
            ok-text="Salvar"
            cancel-text="Cancelar"
            width="36vw"
            :dialog-style="{ minWidth: '420px', maxWidth: '600px' }"
            :confirm-loading="saving"
            @ok="submit"
            @cancel="form.visible = false"
        >
            <a-form layout="vertical">
                <a-form-item label="Nome">
                    <a-input
                        v-model="form.name"
                        :max-length="100"
                        placeholder="Ex.: Ruptura curva A"
                        @pressEnter="submit"
                    />
                </a-form-item>
                <div class="saved-filters-options">
                    <a-checkbox v-model="form.is_shared">
                        Compartilhar com a equipe
                    </a-checkbox>
                    <a-checkbox v-if="form.id" v-model="form.overwrite">
                        Substituir pelos filtros atuais
                    </a-checkbox>
                </div>
                <a-collapse
                    v-if="rangeFields.length && form.ranges"
                    :bordered="false"
                    class="saved-filters-advanced"
                >
                    <a-collapse-panel key="ranges" header="Avançado">
                        <p class="saved-filters-advanced-hint">
                            Limites gravados no filtro. Vazio é sem limite; um
                            número vale como está, mesmo que seja o mínimo ou o
                            máximo atual da base.
                        </p>
                        <a-row
                            v-for="field in rangeFields"
                            :key="field.key"
                            :gutter="8"
                            type="flex"
                            align="middle"
                            class="saved-filters-range"
                        >
                            <a-col :span="10">
                                <div>{{ field.label }}</div>
                                <div
                                    v-if="hasBase(field)"
                                    class="saved-filters-range-base"
                                >
                                    Base: {{ field.base[0] }} a
                                    {{ field.base[1] }}
                                </div>
                            </a-col>
                            <a-col :span="7">
                                <a-input
                                    :value="form.ranges[field.key].min"
                                    placeholder="Sem mínimo"
                                    @input="setBound(field.key, 'min', $event)"
                                    @pressEnter="submit"
                                />
                            </a-col>
                            <a-col :span="7">
                                <a-input
                                    :value="form.ranges[field.key].max"
                                    placeholder="Sem máximo"
                                    @input="setBound(field.key, 'max', $event)"
                                    @pressEnter="submit"
                                />
                            </a-col>
                        </a-row>
                    </a-collapse-panel>
                </a-collapse>
            </a-form>
        </a-modal>
    </a-modal>
</template>

<script>
import SavedFiltersService from "@/services/v3/saved-filters.service";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import {
    rangeBoundsFromPayload,
    applyRangeBounds,
    sanitizeBoundInput,
    parseBound,
    formatBound,
} from "@/helpers/saved-filters";

export default {
    name: "SavedFiltersModal",

    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        screen: {
            type: String,
            required: true,
        },
        items: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        activeId: {
            type: Number,
            default: null,
        },
        activeModified: {
            type: Boolean,
            default: false,
        },
        getPayload: {
            type: Function,
            required: true,
        },
        rangeFields: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            service: new SavedFiltersService(),
            saving: false,
            form: {
                visible: false,
                id: null,
                name: "",
                is_shared: false,
                overwrite: false,
                itemPayload: null,
                basePayload: null,
                ranges: null,
            },
        };
    },

    watch: {
        "form.overwrite"(overwrite) {
            if (this.form.id) {
                this.setBasePayload(
                    overwrite ? this.getPayload() : this.form.itemPayload
                );
            }
        },
    },

    methods: {
        openCreate() {
            this.form = {
                visible: true,
                id: null,
                name: "",
                is_shared: false,
                overwrite: false,
                itemPayload: null,
                basePayload: null,
                ranges: null,
            };
            this.setBasePayload(this.getPayload());
        },
        openEdit(item) {
            this.form = {
                visible: true,
                id: item.id,
                name: item.name,
                is_shared: item.is_shared,
                overwrite: false,
                itemPayload: item.payload,
                basePayload: null,
                ranges: null,
            };
            this.setBasePayload(item.payload);
        },
        setBasePayload(payload) {
            const bounds = rangeBoundsFromPayload(
                payload,
                this.rangeFields.map((field) => field.key)
            );

            this.form.basePayload = payload;
            this.form.ranges = Object.fromEntries(
                Object.entries(bounds).map(([key, [min, max]]) => [
                    key,
                    { min: formatBound(min), max: formatBound(max) },
                ])
            );
        },
        setBound(key, side, event) {
            const value = sanitizeBoundInput(event.target.value);

            event.target.value = value;
            this.form.ranges[key][side] = value;
        },
        hasBase(field) {
            return (
                Array.isArray(field.base) &&
                typeof field.base[0] === "number" &&
                typeof field.base[1] === "number"
            );
        },
        payloadToSave() {
            const bounds = Object.fromEntries(
                Object.entries(this.form.ranges || {}).map(([key, range]) => [
                    key,
                    [parseBound(range.min), parseBound(range.max)],
                ])
            );

            return applyRangeBounds(this.form.basePayload, bounds);
        },
        async submit() {
            if (this.saving) {
                return;
            }

            const name = (this.form.name || "").trim();

            if (!name) {
                this.$message.warning("Informe um nome para o filtro.");
                return;
            }

            this.saving = true;

            try {
                if (this.form.id) {
                    await this.service.update(this.form.id, {
                        name,
                        is_shared: this.form.is_shared,
                        payload: this.payloadToSave(),
                    });

                    if (this.form.overwrite) {
                        this.$emit("saved", this.form.id);
                    }
                } else {
                    const { data } = await this.service.create({
                        screen: this.screen,
                        name,
                        payload: this.payloadToSave(),
                        is_shared: this.form.is_shared,
                    });

                    this.$emit("saved", data.data.id);
                }

                this.form.visible = false;
                this.$message.success("Filtro salvo.");
                this.$emit("changed");
            } catch (err) {
                this.handleError(err, "Não foi possível salvar o filtro.");
            } finally {
                this.saving = false;
            }
        },
        async remove(item) {
            try {
                await this.service.remove(item.id);
                this.$message.success("Filtro excluído.");
                this.$emit("changed");
            } catch (err) {
                this.handleError(err, "Não foi possível excluir o filtro.");
            }
        },
        handleError(err, fallback) {
            ErrorHandlerService.handle(err, this.$store);

            const errors = err?.response?.data?.errors;
            const message = Array.isArray(errors)
                ? errors[0]
                : errors && Object.values(errors).flat()[0];

            this.$message.error(message || fallback);
        },
    },
};
</script>

<style lang="scss" scoped>
.saved-filters-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 12px;
}

.saved-filters-count {
    color: rgba(0, 0, 0, 0.45);
}

.saved-filters-empty {
    padding: 8px 0;
    color: rgba(0, 0, 0, 0.45);
}

.saved-filters-list {
    max-height: 55vh;
    overflow-y: auto;
}

.saved-filters-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px;
    border-left: 3px solid transparent;
    border-radius: 2px;
    cursor: pointer;
    transition: background 0.2s;

    & + & {
        margin-top: 2px;
    }

    &:hover {
        background: #fafafa;
    }

    &.is-active {
        background: #e6f7ff;
        border-left-color: #1890ff;

        .saved-filters-name {
            font-weight: 500;
        }
    }
}

.saved-filters-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: rgba(0, 0, 0, 0.85);
}

.saved-filters-controls {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.saved-filters-check {
    color: #1890ff;
}

.saved-filters-action {
    cursor: pointer;
    color: rgba(0, 0, 0, 0.45);

    &:hover {
        color: #1890ff;
    }
}

.saved-filters-advanced {
    margin-top: 12px;
    background: transparent;
}

.saved-filters-advanced-hint {
    margin-bottom: 12px;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
}

.saved-filters-range + .saved-filters-range {
    margin-top: 8px;
}

.saved-filters-range-base {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
}

.saved-filters-options {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .ant-checkbox-wrapper {
        margin-left: 0;
    }
}
</style>
